'use client';

import { __rest } from '../_virtual/_tslib.js';
import { ELanguage, SEARCH_PARAMS, ROUTE_SEARCH } from './constants.js';
import { encodeKeyword, slugify, transformToLatin } from './helpers.js';

function joinPath(pathComponents) {
    return pathComponents.filter(function (str) { return str !== ''; }).join('-');
}
function composePath(slug, code, suffix) {
    return joinPath([slug, code, suffix]);
}
function _hasInputSearchFilter(searchParams) {
    return {
        keyword: searchParams.query && searchParams.query !== '',
        location: searchParams.locationId && searchParams.locationId > -1,
        category: searchParams.categoryId && searchParams.categoryId > -1,
        isUrgentJob: searchParams.isUrgentJob === true,
        typeWorkingId: searchParams.typeWorkingId && searchParams.typeWorkingId > -1,
        level: searchParams.jobLevelId && searchParams.jobLevelId > -1
    };
}
function determineUrgentJobPage(searchStatus, providedPath) {
    var searchPath = '';
    if (providedPath !== undefined && providedPath) {
        if (providedPath.match(/viec-can-tuyen-gap|urgent-jobs/) && searchStatus.isUrgentJob) {
            searchPath = providedPath;
        }
    }
    else {
        if (window &&
            window.location &&
            window.location.pathname &&
            window.location.pathname.match(/viec-can-tuyen-gap|urgent-jobs/) &&
            searchStatus.isUrgentJob) {
            searchPath = window.location.pathname;
        }
    }
    return searchPath;
}
function buildTypeWorkingSlug(typeWorkingId, typeWorkingSelectData, language) {
    var typeWorkingItem = typeWorkingSelectData.find(function (item) { return item.id === typeWorkingId; });
    var typeWorkingViSlug = slugify(transformToLatin((typeWorkingItem === null || typeWorkingItem === void 0 ? void 0 : typeWorkingItem.name) || ''));
    var typeWorkingSlug = ((language === ELanguage.EN ? typeWorkingItem === null || typeWorkingItem === void 0 ? void 0 : typeWorkingItem.nameEn : typeWorkingViSlug) || '').toLowerCase();
    return typeWorkingSlug;
}
function _buildSearchSlugText(keyword, categorySlug, locationSlug, language, isUrgentJob, typeWorkingSlug, hasAnyFilter) {
    var slug = '';
    var isEn = language === ELanguage.EN;
    var AT = isEn ? 'at' : 'nganh';
    var IN = isEn ? 'in' : 'tai';
    var JOBS = isEn ? 'jobs' : 'viec-lam';
    var URGENT = isEn ? 'urgent' : 'tuyen-gap';
    if (keyword !== '') {
        var filterSlug = typeWorkingSlug;
        if (categorySlug !== '') {
            filterSlug += "".concat(filterSlug !== '' ? '-' : '').concat(AT, "-").concat(categorySlug);
        }
        if (locationSlug !== '') {
            filterSlug += "".concat(filterSlug !== '' ? '-' : '').concat(IN, "-").concat(locationSlug);
        }
        slug += filterSlug !== '' ? "".concat(keyword, "+").concat(filterSlug) : "".concat(keyword, "+");
    }
    else {
        var prefixSlug = joinPath(isEn ? [typeWorkingSlug, categorySlug, JOBS] : [JOBS, typeWorkingSlug, categorySlug]);
        slug = hasAnyFilter ? prefixSlug : '';
        if (categorySlug !== '' && locationSlug !== '') {
            slug = "".concat(prefixSlug, "-").concat(IN, "-").concat(locationSlug);
        }
        else {
            if (locationSlug !== '') {
                slug = "".concat(isEn ? '' : "".concat(prefixSlug, "-")).concat(locationSlug).concat(isEn ? "-".concat(prefixSlug) : '');
            }
        }
    }
    if (isUrgentJob) {
        slug = "".concat(slug, "-").concat(URGENT, "-uj");
    }
    return slug;
}
function _buildSearchCode(searchParams) {
    var _a = searchParams.categoryId, categoryId = _a === void 0 ? -1 : _a, _b = searchParams.locationId, locationId = _b === void 0 ? -1 : _b, _c = searchParams.jobLevelId, jobLevelId = _c === void 0 ? -1 : _c, _d = searchParams.typeWorkingId, typeWorkingId = _d === void 0 ? -1 : _d;
    var categoryCode = categoryId > 0 ? 'i' + categoryId : '';
    var locationCode = locationId > 0 ? 'v' + locationId : '';
    var jobLevelCode = jobLevelId > 0 ? 'l' + jobLevelId : '';
    var typeWorkingCode = typeWorkingId > 0 ? 't' + typeWorkingId : '';
    return categoryCode + locationCode + jobLevelCode + typeWorkingCode;
}
/**
 * Convert searchParams to URL
 * @param searchParams
 * @param selectData
 * @param language
 * @param providedPath
 * @return URL
 */
function buildSearchPath(searchParams, selectData, language, providedPath) {
    if (Object.keys(selectData).length === 0) {
        return '';
    }
    var searchPath = '';
    var keyword = encodeKeyword(searchParams.query || '');
    var langCode = language;
    var isEn = langCode === ELanguage.EN;
    var suffixPath = isEn ? 'en' : 'vn';
    var suffixOnlyKeyword = isEn ? 'kw' : 'kv';
    // detect if search params has keyword, category, location has filtered data rather than default values
    var searchStatus = _hasInputSearchFilter(searchParams);
    searchStatus.isUrgentJob; searchStatus.keyword; var searchStatusOnlyFilter = __rest(searchStatus, ["isUrgentJob", "keyword"]);
    var hasFilter = Object.values(searchStatusOnlyFilter).some(function (hasSet) { return !!hasSet; });
    // Exceptional case: Only keyword provided
    if (searchStatus.keyword && !hasFilter) {
        if (searchStatus.isUrgentJob) {
            var URGENT = isEn ? 'urgent' : 'tuyen-gap';
            return "/".concat(keyword, "-").concat(suffixOnlyKeyword, "-").concat(URGENT, "-uj");
        }
        else {
            return "/".concat(keyword, "-").concat(suffixOnlyKeyword);
        }
    }
    searchPath = determineUrgentJobPage(searchStatus, providedPath);
    if (!searchStatus.keyword && !hasFilter && searchStatus.isUrgentJob) {
        return isEn ? '/urgent-jobs' : '/viec-can-tuyen-gap';
    }
    var categoryId = searchParams.categoryId || -1;
    var categorySlug = categoryId > 0 ? selectData.categoryById[categoryId].slug : '';
    var locationId = searchParams.locationId || -1;
    var locationSlug = locationId > 0 ? selectData.locationById[locationId].slug : '';
    var isUrgentJob = searchParams.isUrgentJob || false;
    var typeWorkingId = searchParams.typeWorkingId || -1;
    var typeWorkingSlug = buildTypeWorkingSlug(typeWorkingId, selectData.typeWorking, language);
    var searchSlug = _buildSearchSlugText(keyword, categorySlug, locationSlug, langCode, isUrgentJob, typeWorkingSlug, hasFilter);
    var searchCodeStr = _buildSearchCode(searchParams);
    if (searchSlug !== '' || searchCodeStr !== '') {
        searchPath = "/".concat(composePath(searchSlug, searchCodeStr, suffixPath));
    }
    searchPath = searchPath.replace('+-', '+');
    return searchPath;
}
function generateCompanyLink(companyId, companySlug, langCode) {
    var prefixPath = langCode === ELanguage.EN ? 'companies' : 'nha-tuyen-dung';
    return "/".concat(prefixPath, "/").concat(companySlug, "-c").concat(companyId);
}
function objToQueryStr(params) {
    if (typeof params !== 'object') {
        return;
    }
    return Object.keys(params)
        .map(function (key) { return "".concat(key, "=").concat(encodeURIComponent(params[key])); })
        .join('&');
}
function buildCompanyLink(companyId, companySlug, companyCCPSlug, langCode, isCCP) {
    if (isCCP === void 0) { isCCP = false; }
    return isCCP ? "/company/".concat(companyCCPSlug) : generateCompanyLink(companyId, companySlug, langCode);
}
var buildCompanyUrl = function (_a) {
    var companyId = _a.companyId, companySlug = _a.companySlug, companyCCPSlug = _a.companyCCPSlug, langCode = _a.langCode, _b = _a.isCCP, isCCP = _b === void 0 ? false : _b;
    var prefix = langCode === ELanguage.EN ? 'companies' : 'nha-tuyen-dung';
    var companyUrl = "/".concat(prefix, "/").concat(slugify(companySlug || ''), "-c").concat(companyId);
    return isCCP ? "/company/".concat(companyCCPSlug) : companyUrl;
};
var buildJobUrl = function (_a) {
    var alias = _a.alias, jobId = _a.jobId, utm = _a.utm, langCode = _a.langCode;
    return "".concat("/".concat(alias, "-").concat(jobId, "-").concat(langCode === ELanguage.EN ? 'jd' : 'jv')).concat(utm);
};
var buildSearchUrl = function (searchParams, language) {
    var params = {};
    if (searchParams.query) {
        params[SEARCH_PARAMS.QUERY] = searchParams.query.replace(/ /g, '-');
    }
    if (searchParams.locationId && searchParams.locationId.length > 0) {
        params[SEARCH_PARAMS.LOCATION] = searchParams.locationId.join('.');
    }
    if (searchParams.categoryId && searchParams.categoryId.length > 0) {
        params[SEARCH_PARAMS.CATEGORY] = searchParams.categoryId.join('.');
    }
    if (searchParams.jobFunctionId && searchParams.jobFunctionId.length > 0) {
        var groupJobFunctionId_1 = [];
        var jobFunctionId_1 = [];
        searchParams.jobFunctionId.map(function (item) {
            groupJobFunctionId_1.push(item.parentId);
            item.childrenIds.map(function (child) { return child !== -1 && jobFunctionId_1.push(child); });
        });
        if (groupJobFunctionId_1.length > 0) {
            params[SEARCH_PARAMS.GROUP_JOB_FUNCTION] = groupJobFunctionId_1.join('.');
        }
        if (jobFunctionId_1.length > 0) {
            params[SEARCH_PARAMS.JOB_FUNCTION] = jobFunctionId_1.join('.');
        }
    }
    if (searchParams.companyIndustries && searchParams.companyIndustries.length > 0) {
        params[SEARCH_PARAMS.INDUSTRY] = searchParams.companyIndustries.join('.');
    }
    if (searchParams.typeWorkingId !== undefined && searchParams.typeWorkingId > -1) {
        params[SEARCH_PARAMS.WORKING_TYPE] = searchParams.typeWorkingId;
    }
    if (searchParams.jobLevelId !== undefined && searchParams.jobLevelId > -1) {
        params[SEARCH_PARAMS.JOB_LEVEL] = searchParams.jobLevelId;
    }
    if (searchParams.isUrgentJob) {
        params[SEARCH_PARAMS.URGENT] = String(searchParams.isUrgentJob);
    }
    if (searchParams.page !== undefined && searchParams.page > 0) {
        params[SEARCH_PARAMS.PAGE] = searchParams.page + 1;
    }
    if (Object.keys(params).length > 0) {
        var paramsString = '?' + new URLSearchParams(params).toString();
        var baseUrl = language === ELanguage.VI ? ROUTE_SEARCH.JOBS_VI : ROUTE_SEARCH.JOBS;
        return baseUrl + paramsString;
    }
    else {
        return language === ELanguage.VI ? ROUTE_SEARCH.RESULT_VI : ROUTE_SEARCH.RESULT;
    }
};
function buildLoginUrl(loginUrl, hash) {
    if (loginUrl === void 0) { loginUrl = ''; }
    var currentUrl = window.location.href;
    return "".concat(loginUrl, "?redirectURL=").concat(encodeURIComponent(currentUrl)).concat(hash);
}

export { buildCompanyLink, buildCompanyUrl, buildJobUrl, buildLoginUrl, buildSearchPath, buildSearchUrl, generateCompanyLink, objToQueryStr };
//# sourceMappingURL=urlBuilder.js.map
